import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useExchangeRates } from '../services/exchangeRates';
import { useFreshCities, useFreshCountries, useFreshManagers, useFreshTalent, UserContext } from '../services/firebase';
import { contentTags } from '../shared/grailConstants';
import { List, Platform, Talent, User } from '../shared/types';
import { getRosterList } from '../utils/roster';

import Layout from '../components/Layout';
import { AppContainer } from '../components/AppContainer';
import { FormCheckbox } from '@/components/forms/FormCheckbox';
import { ContentTagsCell, ContentTagsCellSkeleton, CreatorCell, CreatorCellSkeleton, PlatformCell, PlatformCellSkeleton } from '@/components/tableKit';
import { TalentBrowse, type TalentBrowseProps, type TalentRow } from '@/components/talentBrowse';
import { Heading, Overline, TextLink } from '@/components/typography';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toast';

const ROSTER_PLATFORMS: Platform[] = ["TikTok", "Instagram", "YouTube"];

function rosterHeadingName(manager: User | undefined, managerUid: string) {
    if (manager?.firstName) {
        return `${manager.firstName} ${manager.lastName}'s Roster`;
    }
    return `${manager?.email || managerUid}'s Roster`;
}

function RosterDetailsCell({ talent }: { talent: Talent }) {
    return (
        <div>
            {talent.managers?.length ? (
                <div className="mb-4">
                    <Overline>Managed By</Overline>
                    {talent.managers.map((manager) => (
                        <div key={manager.id}>{manager.email}</div>
                    ))}
                </div>
            ) : null}
            {talent.hideUser ? (
                <div className="text-sm uppercase text-destructive">
                    Hidden from Clients
                </div>
            ) : null}
            {talent.deleted ? (
                <div className="text-sm uppercase text-destructive">
                    Deleted from Platform
                </div>
            ) : null}
            <TextLink
                className="mt-4 block"
                href={`/edit-talent/${talent.id}`}
                target="_blank"
                rel="noreferrer"
            >
                View/Edit Details
            </TextLink>
        </div>
    );
}

function RosterDetailsCellSkeleton() {
    return (
        <div>
            <Skeleton className="mb-2 h-3 w-16" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="mt-4 h-4 w-28" />
        </div>
    );
}

const ROSTER_COLUMNS: NonNullable<TalentBrowseProps["columns"]> = [
    {
        id: "creator",
        header: "Creator",
        cell: (row) => <CreatorCell talent={row.talent} />,
        skeleton: <CreatorCellSkeleton />,
    },
    ...ROSTER_PLATFORMS.map((platform) => ({
        id: platform,
        header: platform,
        cell: (row: TalentRow) => <PlatformCell platform={platform} talent={row.talent} />,
        skeleton: <PlatformCellSkeleton />,
    })),
    {
        id: "content",
        header: "Content",
        cell: (row) => (
            <>
                {row.talent.category === "Micro-influencer" ? (
                    <Badge variant="outline" className="mr-1 mb-1 uppercase">
                        Micro-Influencer
                    </Badge>
                ) : null}
                <ContentTagsCell tags={row.talent.contentTags} />
            </>
        ),
        skeleton: <ContentTagsCellSkeleton />,
    },
    {
        id: "details",
        header: "",
        cell: (row) => <RosterDetailsCell talent={row.talent} />,
        skeleton: <RosterDetailsCellSkeleton />,
    },
];

export default function Roster() {
    const { managerUid, creatorUid } = useParams<{ managerUid?: string, creatorUid?: string }>();
    return <RosterPage key={`${managerUid ?? "all"}:${creatorUid ?? ""}`} />;
}

function RosterPage() {
    const user = useContext(UserContext);
    const isGrailTeam = Boolean(user?.roles?.manager || user?.roles?.admin || user?.roles?.externalManager || user?.roles?.superAdmin);

    const { managerUid, creatorUid } = useParams<{ managerUid?: string, creatorUid?: string }>();
    const [showHiddenAndDeleted, setShowHiddenAndDeleted] = useState(false);
    const managersQuery = useFreshManagers({ enabled: Boolean(isGrailTeam) });
    const managers = managersQuery.flatData;

    const manager = managerUid ? managers.find((man) => man.id === managerUid) : undefined;

    const talentQuery = useFreshTalent({ includeHidden: true, includeDeleted: true, managerId: managerUid, creatorUserId: creatorUid });
    const talent = talentQuery.flatData;
    const browseTalent = useMemo(
        () => (showHiddenAndDeleted ? talent : talent.filter((tal) => !tal.deleted && !tal.hideUser)),
        [showHiddenAndDeleted, talent],
    );

    const exchangeRates = useExchangeRates();
    const countriesQuery = useFreshCountries();
    const countryList = countriesQuery.flatData.filter((country) => country.liveOnAirtable).map((country) => country.label).sort();
    const cityQuery = useFreshCities();
    const cityList = cityQuery.flatData.map((cityDoc) => cityDoc.city).sort();
    const tagList = contentTags;

    const lists: List[] = useMemo(() => {
        if (managerUid && user?.id === managerUid) {
            return [getRosterList(managerUid, talent)]
        }
        if (managerUid) {
            return [{ id: "my-roster", name: rosterHeadingName(manager, managerUid), active: true }];
        }
        return [{ id: "", name: "All", active: true }];
    }, [managerUid, user?.id, talent, manager]);

    const defaultListId = managerUid ? "my-roster" : "";
    // Same latch as Campaigns: start true so the first paint cannot report ready. Clear when sliced rows exist, the query finished with no persist rows, or the query failed (toast still fires; the table must reach showResult).
    const [talentLoading, setTalentLoading] = useState(true)
    useEffect(() => {
        if (talent.length > 0 || talentQuery.isError) {
            setTalentLoading(false)
            return
        }
        const persistCount = Array.isArray(talentQuery.data) ? talentQuery.data.length : 0
        if (!talentQuery.isLoading && persistCount === 0 && talentQuery.status === "success") {
            setTalentLoading(false)
        }
    }, [talent.length, talentQuery.data, talentQuery.isError, talentQuery.isLoading, talentQuery.status])

    useEffect(() => {
        if (!talentQuery.error) {
            return;
        }
        toast.add({
            type: "error",
            title: "Couldn’t load this roster",
            description: "Refresh and try again.",
        });
        console.error(talentQuery.error);
    }, [talentQuery.error]);

    return (
        <Layout gray={true}>
            <AppContainer className="mt-12 mb-12">
                {manager ? (
                    <Heading type="h1" className="mb-4">
                        {rosterHeadingName(manager, managerUid || "")}:
                    </Heading>
                ) : null}

                <FormCheckbox
                    label="Show Hidden/Deleted Creators"
                    checked={showHiddenAndDeleted}
                    onChange={setShowHiddenAndDeleted}
                    fieldClassName="mb-4"
                />

                <TalentBrowse
                    talent={browseTalent}
                    lists={lists}
                    platforms={ROSTER_PLATFORMS}
                    campaignType="Song"
                    currency="USD"
                    exchangeRates={exchangeRates}
                    defaultListId={defaultListId}
                    columns={ROSTER_COLUMNS}
                    requirePlatformMembership={false}
                    isLoading={talentLoading}
                    filterSheet={{
                        showPriceChips: true,
                        currency: "USD",
                        countryList,
                        tagList,
                        cityList,
                    }}
                    empty="No creators to show."
                />
            </AppContainer>
        </Layout>
    );
}
